import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ArrowDown, ArrowRight, BarChart3, Coffee, Gamepad2, MapPinned, Megaphone, Palette, Pause, Play, QrCode, Smartphone, Sparkles, Trophy, WandSparkles, Waypoints } from 'lucide-react'
import { Link } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { MediaPlaceholder } from '../components/MediaPlaceholder'
import { CTASection, Eyebrow, SectionTitle } from '../components/Shared'
import { games, insights, plans, services } from '../data/content'
import heroImage from '../../assets/clg_generated_01.png'
import productsImage from '../../assets/clg_generated_04.png'
import logo from '../../assets/logo_clg.png'
import waitImage from '../../assets/stock/espera-unsplash.jpg'
import activationImage from '../../assets/stock/ativacao-unsplash.jpg'
import eventImage from '../../assets/stock/evento-unsplash.jpg'
import phygitalImage from '../../assets/stock/figital-unsplash.jpg'

gsap.registerPlugin(ScrollTrigger)

function Hero() {
  const root = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.innerWidth < 760) return
    const context = gsap.context(() => {
      const opening = root.current?.querySelector('.hero-opening')
      const story = root.current?.querySelector('.hero-story-card')
      const conclusion = root.current?.querySelector('.hero-conclusion')
      const flightLogo = root.current?.querySelector<HTMLElement>('.hero-flight-logo')
      const headerLogo = document.querySelector<HTMLElement>('#brand-target')
      if (!opening || !story || !conclusion || !flightLogo || !headerLogo) return

      gsap.set([story, conclusion], { autoAlpha: 0, y: 28, pointerEvents: 'none' })
      gsap.set(opening, { autoAlpha: 1, y: 0 })
      gsap.set(headerLogo, { autoAlpha: 0 })

      const logoTarget = () => {
        const from = flightLogo.getBoundingClientRect()
        const to = headerLogo.getBoundingClientRect()
        return { x: to.left - from.left, y: to.top - from.top, scale: to.width / from.width }
      }

      const tl = gsap.timeline({ scrollTrigger: { trigger: root.current, start: 'top top', end: '+=220%', scrub: .75, pin: true, anticipatePin: 1, invalidateOnRefresh: true } })
      tl.addLabel('brand')
        .to('.hero-photo', { scale: 1.08, xPercent: -2, filter: 'brightness(.72) saturate(1.08)', duration: 1 }, 'brand')
        .to(flightLogo, { x: () => logoTarget().x, y: () => logoTarget().y, scale: () => logoTarget().scale, transformOrigin: 'top left', ease: 'power2.inOut', duration: .2 }, 'brand')
        .to(headerLogo, { autoAlpha: 1, duration: .04 }, .17)
        .to(flightLogo, { autoAlpha: 0, duration: .04 }, .18)
        .to(opening, { autoAlpha: 0, y: -28, pointerEvents: 'none', duration: .1 }, .25)
        .to(story, { autoAlpha: 1, y: 0, pointerEvents: 'auto', duration: .1 }, .35)
        .to(story, { autoAlpha: 1, duration: .18 }, .45)
        .to(story, { autoAlpha: 0, y: -28, pointerEvents: 'none', duration: .1 }, .64)
        .to(conclusion, { autoAlpha: 1, y: 0, pointerEvents: 'auto', duration: .12 }, .75)

      Promise.all([document.fonts.ready, (document.querySelector('.hero-photo') as HTMLImageElement)?.decode?.().catch(() => undefined)]).then(() => ScrollTrigger.refresh())
    }, root)
    return () => context.revert()
  }, [])

  return <section className="hero hero-photo-version" ref={root}>
    <img className="hero-photo" src={heroImage} alt="Pessoa interagindo com um game em um totem durante um evento" />
    <div className="hero-shade" />
    <img className="hero-flight-logo" src={logo} alt="" aria-hidden="true" />
    <div className="hero-copy-stack">
      <div className="hero-opening">
        <Eyebrow>Experiências jogáveis de marca</Eyebrow>
        <h1>Sua marca pode ser vista.<br />Ou pode ser <em>vivida.</em></h1>
        <p>Games personalizados para transformar espera, circulação e eventos em momentos que as pessoas querem compartilhar.</p>
        <div className="button-row"><Link className="button" to="/contato">Pedir proposta <ArrowRight size={18} /></Link><Link className="hero-link" to="/games">Conhecer os games</Link></div>
      </div>
      <div className="hero-story-card"><span>01 — O CONVITE</span><h2>Um toque começa<br />a brincadeira.</h2><p>A tela chama. A mecânica envolve. O espaço ao redor começa a responder.</p></div>
      <div className="hero-conclusion"><Eyebrow>Do intervalo ao encontro</Eyebrow><h2>Jogar aproxima.<br /><em>Sua marca participa.</em></h2><p>Experiências breves, competitivas e feitas para acontecer também fora da tela.</p><Link className="button" to="/contato">Criar uma experiência <ArrowRight size={18} /></Link></div>
    </div>
    <div className="scroll-cue"><ArrowDown size={16} /> role para descobrir</div>
  </section>
}

const serviceScenes = services.map((service, index) => ({
  ...service,
  ...(index === 0 ? {
    short: 'Espera', context: 'Recepções · filas · restaurantes', cta: 'Ver solução para esperas', icon: Coffee,
    image: waitImage,
    alt: 'Duas pessoas sorrindo e compartilhando uma experiência no celular em um café',
    credit: 'Vitaly Gariev / Unsplash', creditUrl: 'https://unsplash.com/photos/TSRu3gwVdEQ',
  } : index === 1 ? {
    short: 'Ativação', context: 'Campanhas · produtos · lançamentos', cta: 'Criar uma ativação jogável', icon: Megaphone,
    image: activationImage,
    alt: 'Grupo de amigos reunido ao redor de um celular durante um encontro',
    credit: 'Vitaly Gariev / Unsplash', creditUrl: 'https://unsplash.com/photos/lVgif0iVqrs',
  } : index === 2 ? {
    short: 'Evento', context: 'Telão · ranking · torcida', cta: 'Transformar evento em arena', icon: Trophy,
    image: eventImage,
    alt: 'Pessoas participando de jogos de arcade em um ambiente iluminado',
    credit: 'Valiant Lambda / Unsplash', creditUrl: 'https://unsplash.com/photos/PQh2CLPFJkE',
  } : {
    short: 'Figital', context: 'Celular · totem · espaço físico', cta: 'Conectar os pontos da jornada', icon: Waypoints,
    image: phygitalImage,
    alt: 'Pessoa jogando em uma máquina de arcade enquanto outra pessoa acompanha',
    credit: 'Brittani Burns / Unsplash', creditUrl: 'https://unsplash.com/photos/gQG5VKXPFaA',
  }),
}))

function ServicesShowcase() {
  const [activeIndex, setActiveIndex] = useState(0)
  const [autoEnabled, setAutoEnabled] = useState(() => typeof window === 'undefined' || !window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  const [isInteracting, setIsInteracting] = useState(false)
  const tabs = useRef<Array<HTMLButtonElement | null>>([])
  const active = serviceScenes[activeIndex]
  const ActiveIcon = active.icon

  useEffect(() => {
    if (!autoEnabled || isInteracting) return
    const timer = window.setTimeout(() => setActiveIndex((current) => (current + 1) % serviceScenes.length), 6500)
    return () => window.clearTimeout(timer)
  }, [activeIndex, autoEnabled, isInteracting])

  const moveTo = (index: number) => {
    const nextIndex = (index + serviceScenes.length) % serviceScenes.length
    setActiveIndex(nextIndex)
    tabs.current[nextIndex]?.focus()
  }

  return <section className="section services-section" id="cenarios">
    <div className="container services-editorial">
      <div className="services-intro">
        <Eyebrow>Onde a marca entra em jogo</Eyebrow>
        <h2><span>Uma plataforma.</span><em>Muitos encontros possíveis.</em></h2>
        <p>Da espera em um estabelecimento à ativação em grandes eventos, escolha um cenário e veja como a experiência muda de forma sem perder o propósito.</p>
        <div className="services-instruction"><Gamepad2 aria-hidden="true" /><span>Escolha uma fase para explorar</span></div>
      </div>

      <div
        className="scenario-selector"
        data-running={autoEnabled && !isInteracting}
        onMouseEnter={() => setIsInteracting(true)}
        onMouseLeave={() => setIsInteracting(false)}
        onFocusCapture={() => setIsInteracting(true)}
        onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setIsInteracting(false) }}
      >
        <div className="scenario-stage">
          <div className="scenario-visual" key={`image-${active.number}`}>
            <img src={active.image} alt={active.alt} width="1800" height="1200" loading="lazy" decoding="async" />
            <div className="scenario-photo-label"><span>Imagem provisória</span><a href={active.creditUrl} target="_blank" rel="noreferrer">{active.credit}</a></div>
            <div className="scenario-reticle" aria-hidden="true"><i /><i /><i /><i /></div>
          </div>
          <div className="scenario-copy" key={`copy-${active.number}`} role="tabpanel" id="service-panel" aria-labelledby={`service-tab-${activeIndex}`} tabIndex={0}>
            <div className="scenario-kicker"><span>{active.number}</span><ActiveIcon aria-hidden="true" /> <b>{active.short}</b></div>
            <h3>{active.title}</h3>
            <p>{active.text}</p>
            <div className="scenario-context"><span>Funciona bem em</span><strong>{active.context}</strong></div>
            <Link to={`/solucoes#solucao-${activeIndex + 1}`} className="scenario-action">{active.cta}<ArrowRight aria-hidden="true" /></Link>
          </div>
        </div>

        <div className="scenario-tabs" role="tablist" aria-label="Cenários de uso do CLG">
          {serviceScenes.map((scene, index) => {
            const Icon = scene.icon
            return <button
              key={scene.number}
              ref={(node) => { tabs.current[index] = node }}
              type="button"
              role="tab"
              id={`service-tab-${index}`}
              aria-controls="service-panel"
              aria-selected={activeIndex === index}
              tabIndex={activeIndex === index ? 0 : -1}
              onClick={() => setActiveIndex(index)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight') { event.preventDefault(); moveTo(activeIndex + 1) }
                if (event.key === 'ArrowLeft') { event.preventDefault(); moveTo(activeIndex - 1) }
                if (event.key === 'Home') { event.preventDefault(); moveTo(0) }
                if (event.key === 'End') { event.preventDefault(); moveTo(serviceScenes.length - 1) }
              }}
            >
              <span>{scene.number}</span><Icon aria-hidden="true" /><strong>{scene.short}</strong>
            </button>
          })}
        </div>
        <div className="scenario-autoplay">
          <span className="scenario-progress" aria-hidden="true"><i key={`${active.number}-${autoEnabled}-${isInteracting}`} /></span>
          <button type="button" onClick={() => setAutoEnabled((enabled) => !enabled)} aria-label={autoEnabled ? 'Pausar transição automática' : 'Reproduzir transição automática'}>
            {autoEnabled ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
            <span>{autoEnabled ? (isInteracting ? 'Pausado durante a interação' : 'Transição automática') : 'Transição pausada'}</span>
          </button>
        </div>
      </div>
    </div>
  </section>
}

export function Home() {
  const planIcons = [Gamepad2, Palette, WandSparkles]
  return <>
    <Hero />
    <section className="section light"><div className="container split-intro"><SectionTitle eyebrow="Não é sobre prender" title={<>É sobre fazer aquele momento <em>valer.</em></>} /><div><p className="lead dark-lead">O CLG transforma espera, circulação e ativações em experiências breves, voluntárias e compartilhadas.</p><p>Enquanto a tela convida para jogar, o ranking, a torcida e a conversa devolvem a experiência ao espaço real.</p><Link className="text-link" to="/advergames">Entenda o que é um advergame <ArrowRight size={16} /></Link></div></div></section>
    <ServicesShowcase />
    <section className="section dark game-showcase"><div className="container"><SectionTitle titleColumns dark tone="cyan" eyebrow="Fábrica de experiências" title={<span className="title-pair"><span>Mecânicas conhecidas.</span><em>Histórias que só a sua marca conta.</em></span>} text="Uma diversidade de games configuráveis para diferentes campanhas, espaços e públicos. O catálogo evolui junto com as possibilidades de ativação." />
      <div className="game-preview"><div className="real-media"><img src={productsImage} alt="Conjunto de interfaces e formatos de games personalizáveis do CLG" /><span>Imagem conceitual — substituir por capturas finais quando disponíveis</span></div><div className="game-mini-list">{games.slice(0,5).map((g,i) => <div key={g.slug}><span>0{i+1}</span><g.icon /><b>{g.name}</b><small>{g.type}</small></div>)}<Link to="/games" className="button button-ghost">Conhecer alguns games <ArrowRight size={17} /></Link></div></div>
    </div></section>
    <section className="section dark roadmap-section"><div className="container"><SectionTitle titleColumns dark tone="yellow" eyebrow="Como acontece" title={<span className="title-pair"><span>Do briefing.</span><em>Ao play.</em></span>} text="Um processo consultivo, claro e progressivo: primeiro entendemos o momento; depois colocamos a experiência em movimento." />
      <div className="steps roadmap"><div><b>01</b><Sparkles /><h3>Entendemos o momento</h3><p>Contexto, público, espaço e objetivo vêm antes da mecânica.</p></div><div><b>02</b><Gamepad2 /><h3>Escolhemos a experiência</h3><p>Game pronto, customização ou projeto temático.</p></div><div><b>03</b><QrCode /><h3>Colocamos no ar</h3><p>Acesso simples por link ou QR Code, com operação adequada ao ambiente.</p></div><div><b>04</b><BarChart3 /><h3>Medimos o que importa</h3><p>Acessos, partidas e resultados compatíveis com os módulos contratados.</p></div></div>
    </div></section>
    <section className="section plans-home"><div className="container"><SectionTitle titleColumns tone="green" eyebrow="Três caminhos" title={<span className="title-pair"><span>Comece pela mecânica.</span><em>Construa o seu pacote.</em></span>} text="Escolha o ponto de partida e acrescente somente os módulos que fazem sentido para a campanha." /><div className="plan-grid">{plans.map((p,index) => { const Icon = planIcons[index]; return <article className={p.featured ? 'plan-card featured' : 'plan-card'} key={p.title}><div className="plan-icon"><Icon /></div><small>{p.label}</small><h3>{p.title}</h3><p>{p.text}</p><ul>{p.points.map(x => <li key={x}>{x}</li>)}</ul><Link to="/contato" className="plan-action">Pedir proposta</Link></article> })}</div><div className="modules"><span><Smartphone /> Pacotes flexíveis de games</span><span><QrCode /> Captação opcional</span><span><BarChart3 /> Relatórios</span><span><MapPinned /> Experiência presencial</span></div></div></section>
    <section className="section dark cases-home"><div className="container"><SectionTitle titleColumns dark tone="yellow" eyebrow="Prova em construção" title={<span className="title-pair"><span>Casos reais.</span><em>Resultados com contexto.</em></span>} text="Esta estrutura receberá projetos, imagens e métricas aprovadas. Cada caso será apresentado com contexto, solução e resultado verificável." /><div className="case-grid">{['foto da ativação','público e produto','ambiente e ranking'].map((label,index) => <article className="case-placeholder-card" key={label}><MediaPlaceholder label={`Caso real 0${index+1} — ${label}`} /><div><span>CASO 0{index+1}</span><h3>Projeto em preparação</h3><p>Conteúdo e resultados serão inseridos após aprovação.</p></div></article>)}</div><Link className="button button-ghost cases-link" to="/casos">Conhecer a estrutura dos casos</Link></div></section>
    <section className="section insight-strip"><div className="container"><SectionTitle eyebrow="Insights" title={<>Ideias para marcas<br />que querem <em>participar.</em></>} /><div className="insight-grid">{insights.map((item,i) => <article key={item.title}><MediaPlaceholder label={`Capa do artigo 0${i+1}`} ratio="square" /><small>{item.category}</small><h3>{item.title}</h3><p>{item.excerpt}</p><Link to="/insights">Ler artigo <ArrowRight size={15} /></Link></article>)}</div></div></section>
    <CTASection />
  </>
}
